// ONE PARTNERS VIEW (owner, 2026-10-16: "merge both UIs of partners tab and
// dashboard & Schedule partners in 1 partner view that everyone links to, and
// make sure all functions are existing from both places").
//
// Until today there were two pages both called "Partners":
//   /participants/:projectId   one project's roster (Dashboard & Schedule,
//                              Scheduler, Glance) - pages/Participants/
//                              ProjectParticipantsPage
//   /partner-management        the account's partner organisations and their
//                              people across every project (the Partners
//                              tab) - pages/Partners/PartnerManagementPage
//
// Both now live HERE, at /partners, and every link in the app comes here:
//   ?projectId=<id>   the project roster - every function it had (add / copy /
//                     import partners, people, sign-in links, access, rates,
//                     profile, crew, move, invite, oversight, recipients...)
//   ?view=all         every partner across your projects - every function the
//                     old Partners tab had (people across projects, which
//                     projects a person is on, grant account, access audit,
//                     clone, coordination access...)
// The two old addresses redirect here (App.jsx), so a bookmark or an email
// link still lands on the right part.
//
// THE TWO HALVES KEEP THEIR OWN RULES. The project roster is for anyone who
// can see the project (read-only unless they may edit partners); "Across all
// projects" is for super admins and company admins, as it always was - the
// server enforces both, this page only decides what to show.
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Select, Segmented, Empty } from "antd";
import { TeamOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import ProjectParticipantsPage from "pages/Participants/ProjectParticipantsPage";
import PartnerManagementPage from "pages/Partners/PartnerManagementPage";
import { getRememberedProject } from "helpers/projectSelection";
import { matchOption } from "components/SearchSelect/searchSelect";

export const PROJECT_VIEW = "project";
export const ALL_VIEW = "all";

/** The address of the Partners view for a project (or the account view). */
export function partnersPath(projectId) {
  return projectId ? `/partners?projectId=${projectId}` : "/partners?view=all";
}

function useProjects() {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    let alive = true;
    fetch(`${url}/projects/picker`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((b) => { if (alive) setProjects(Array.isArray(b) ? b : (b && (b.projects || b.data)) || []); })
      .catch(() => { if (alive) setProjects([]); });
    return () => { alive = false; };
  }, []);
  return projects;
}

export default function PartnersHub() {
  const history = useHistory();
  const { search } = useLocation();
  const userInfo = useSelector((s) => s.user && s.user.userInfo);
  const isAdmin = !!(userInfo && (userInfo.admin || userInfo.companyAdmin));
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const userId = userInfo && (userInfo._id || userInfo.id);
  const projectId = params.get("projectId") || "";
  const projects = useProjects();
  // No project named and not the account view: the remembered project, so a
  // bare /partners opens where the person was working.
  useEffect(() => {
    if (projectId || params.get("view") === ALL_VIEW) return;
    const last = getRememberedProject(userId);
    if (last) history.replace(partnersPath(last));
    else if (isAdmin) history.replace(partnersPath(""));
  }, [projectId, params, userId, isAdmin, history]);

  const view = projectId ? PROJECT_VIEW : (params.get("view") === ALL_VIEW && isAdmin ? ALL_VIEW : PROJECT_VIEW);
  const go = (v, pid = projectId) => history.push(v === ALL_VIEW ? partnersPath("") : partnersPath(pid || getRememberedProject(userId) || ""));
  const projectOptions = projects.map((p) => ({ value: String(p._id || p.id), label: p.title || p.name || String(p._id) }));

  return (
    <div>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "16px 24px 0" }}>
        <CoordinationBreadcrumbs current="Partners" />
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
          <h2 style={{ margin: 0 }}><TeamOutlined /> Partners</h2>
          <Segmented
            value={view}
            onChange={(v) => go(v)}
            options={[
              { label: "This project", value: PROJECT_VIEW },
              ...(isAdmin ? [{ label: "Across all projects", value: ALL_VIEW }] : []),
            ]}
          />
          {view === PROJECT_VIEW ? (
            <Select filterOption={matchOption}
              showSearch optionFilterProp="label" style={{ minWidth: 280 }} placeholder="Pick a project"
              value={projectId || undefined} options={projectOptions}
              onChange={(pid) => history.push(partnersPath(pid))}
            />
          ) : null}
        </div>
      </div>
      {view === ALL_VIEW ? (
        <PartnerManagementPage embedded />
      ) : projectId ? (
        <ProjectParticipantsPage key={projectId} projectId={projectId} embedded />
      ) : (
        <div style={{ padding: 48 }}><Empty description="Pick a project to see its partners" /></div>
      )}
    </div>
  );
}
